import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Trash2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import employeesCanonical from "@/api/employees-canonical";
import { formatApiError } from "@/api/client";
import { SOURCE_LABELS } from "./AliasBadge";

// Alias source options exposed to the admin UI. Order matters: OPER first
// (most common — it's the operator ID from the scanners.xlsx roster), then
// the raw report codes in the natural read order. Kept in sync with the
// backend `AliasSource` enum. When adding a new option, mirror it in
// `AliasBadge.SOURCE_LABELS` so the row pills render correctly.
export const SOURCE_OPTIONS = [
  { value: "OPER", label: "OPER (Z## scanner ID)" },
  { value: "GENDETAILED_PROD", label: "GENDETAILED (Z##)" },
  { value: "IC385DETAIL", label: "IC385DETAIL (3 dígitos)" },
  { value: "IC385P2DETAIL", label: "IC385P2DETAIL (3 dígitos)" },
  { value: "TE350", label: "TE350 (Staff ID)" },
  { value: "TIME_CLOCK_YEARLY", label: "Time Clock Anual" },
  { value: "TIME_CLOCK_MONTH", label: "Time Clock Mensual" },
];

/**
 * Phase 5.11 — Modal "+ Agregar empleado".
 *
 * Crea un canónico nuevo (marcado merged_manually=True) y dispara un
 * backfill automático en backend que asigna `canonical_id` a las tx/te
 * cuyos (employee_id, source_report) coincidan con cada alias.
 *
 * The component is unmounted whenever `open` becomes false (the Dialog
 * primitive does that via `forceMount={false}`) so the local state always
 * starts fresh — no manual reset effect needed.
 */
export default function CreateEmployeeDialog({ open, onOpenChange, onCreated }) {
  if (!open) return null;
  return (
    <CreateEmployeeDialogBody
      open={open}
      onOpenChange={onOpenChange}
      onCreated={onCreated}
    />
  );
}

function CreateEmployeeDialogBody({ open, onOpenChange, onCreated }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [aliases, setAliases] = useState([{ code: "", source: "OPER" }]);
  const [saving, setSaving] = useState(false);

  const cleanedAliases = useMemo(
    () =>
      aliases
        .map((a) => ({ code: (a.code || "").trim(), source: a.source }))
        .filter((a) => a.code && a.source),
    [aliases]
  );

  const canSave = name.trim().length > 0 && !saving;

  const updateAlias = (idx, patch) =>
    setAliases((curr) => curr.map((a, i) => (i === idx ? { ...a, ...patch } : a)));

  const addAlias = () =>
    setAliases((curr) => [...curr, { code: "", source: "OPER" }]);

  const removeAlias = (idx) =>
    setAliases((curr) => curr.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        canonical_name: name.trim(),
        aliases: cleanedAliases,
        department_ids: [],
      };
      const r = await employeesCanonical.create(payload);
      const { reattributed_tx = 0, reattributed_te = 0, employee } = r.data || {};
      toast.success(
        t("employeesCanonical.createdToast", {
          name: employee?.canonical_name || name.trim(),
          tx: reattributed_tx.toLocaleString(),
          te: reattributed_te.toLocaleString(),
        })
      );
      onOpenChange(false);
      if (typeof onCreated === "function") await onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="create-employee-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {t("employeesCanonical.createTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("employeesCanonical.createDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="create-employee-name" className="text-xs">
              {t("employeesCanonical.createNameLabel")}
            </Label>
            <Input
              id="create-employee-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("employeesCanonical.createNamePlaceholder")}
              data-testid="create-employee-name-input"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">
                {t("employeesCanonical.createAliasesLabel")}
              </Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addAlias}
                data-testid="create-employee-add-alias"
                className="h-7"
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                {t("employeesCanonical.createAddAlias")}
              </Button>
            </div>

            <div className="space-y-2 rounded-md border p-3">
              {aliases.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {t("employeesCanonical.createNoAliases")}
                </p>
              )}
              {aliases.map((a, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2"
                  data-testid={`create-employee-alias-row-${idx}`}
                >
                  <Select
                    value={a.source}
                    onValueChange={(v) => updateAlias(idx, { source: v })}
                  >
                    <SelectTrigger
                      className="h-9 w-[210px]"
                      data-testid={`create-employee-alias-source-${idx}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SOURCE_OPTIONS.map((o) => (
                        <SelectItem
                          key={o.value}
                          value={o.value}
                          data-testid={`create-employee-alias-source-option-${idx}-${o.value}`}
                        >
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={a.code}
                    onChange={(e) => updateAlias(idx, { code: e.target.value })}
                    placeholder={t("employeesCanonical.createAliasCodePlaceholder")}
                    className="h-9 flex-1 font-mono"
                    data-testid={`create-employee-alias-code-${idx}`}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => removeAlias(idx)}
                    data-testid={`create-employee-alias-remove-${idx}`}
                    className="h-9 w-9 text-muted-foreground hover:text-destructive"
                    aria-label={t("common.delete")}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {t("employeesCanonical.createBackfillHint")}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="create-employee-cancel"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={submit}
            disabled={!canSave}
            data-testid="create-employee-save"
          >
            {saving ? t("common.loading") : t("employeesCanonical.createSave")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
